import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useSpace } from "../hooks/useSpace";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import {
  useSpaceFormOptions,
  MAX_LESSONS_PER_SPACE,
} from "../hooks/useSpaceFormOptions";

export default function CreateSpacePage() {
  const navigate = useNavigate();
  const [lessonContext, setLessonContext] = useState("");
  const [spaceName, setSpaceName] = useState("");
  const [description, setDescription] = useState("");
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isSavingDraft, setIsDraftSubmitting] = useState(false);
  const [isLaunching, setIsLaunchSubmitting] = useState(false);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<number[]>(
    [],
  );
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([]);
  const { showNotification } = useNotificationStore();
  const { createSpace } = useSpace();
  const { classrooms, classroomsLoading, lessons, lessonsLoading } =
    useSpaceFormOptions();

  const handleCreate = async (draft: boolean) => {
    if (pending.current) return;
    setError(null);
    if (!draft && selectedClassroomIds.length === 0) {
      showNotification("Classroom is required.", NotificationType.WARNING);
      setError("Choose at least one classroom to launch the Space.");
      return;
    }
    const setSubmitting = draft ? setIsDraftSubmitting : setIsLaunchSubmitting;
    pending.current = true;
    setSubmitting(true);
    try {
      const result = await createSpace(
        selectedClassroomIds,
        selectedLessonIds,
        lessonContext,
        spaceName,
        draft,
        description,
      );
      showNotification(
        draft ? "Draft saved!" : "Space created!",
        NotificationType.SUCCESS,
      );
      navigate(draft ? "/spaces" : `/spaces/${result.spaceId}`);
    } catch {
      setError(
        "Failed to create Space. Your entries are preserved; please try again.",
      );
      showNotification("Failed to create space", NotificationType.WARNING);
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-[6px] border border-gray-200 px-4 py-3 text-sm font-mulish text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent bg-white transition";

  return (
    <div className="et-space-create">
      <div className="et-space-create-heading">
        <h1 className="text-3xl font-mulish font-bold text-darkgreen">
          Launch a new{" "}
          <span className="font-libre-baskerville italic">Space</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400 font-mulish">
          Configure the space you'll lead with your class.
        </p>
      </div>

      <button
        type="button"
        onClick={() => navigate("/spaces")}
        className="et-space-create-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Spaces
      </button>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCreate(false);
        }}
        className="et-space-create-form"
      >
        {error && (
          <p role="alert" className="et-group-error et-space-create-error">
            {error}
          </p>
        )}
        <div className="et-space-main-fields">
          {/* Space name */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="create-space-name"
              className="text-sm font-bold font-mulish text-gray-800"
            >
              Space name
            </label>
            <input
              id="create-space-name"
              type="text"
              value={spaceName}
              onChange={(e) => setSpaceName(e.target.value)}
              placeholder="e.g. Period 2 — Fractions warm-up"
              maxLength={100}
              className={inputClass}
            />
            <span className="text-xs text-gray-400 font-mulish">
              Students see this name when they join.
            </span>
          </div>

          {/* Classrooms */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="create-space-classrooms"
              className="text-sm font-bold font-mulish text-gray-800"
            >
              Classrooms <span className="text-red-400">*</span>
            </label>
            <MultiSelectDropdown
              id="create-space-classrooms"
              options={classrooms}
              selected={selectedClassroomIds}
              onChange={setSelectedClassroomIds}
              placeholder="Select one or more classrooms…"
              loading={classroomsLoading}
            />
            <span className="text-xs text-gray-400 font-mulish">
              Pick multiple classrooms to launch the same space across them.
            </span>
          </div>

          {/* Bites (lessons) */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="create-space-lessons"
              className="text-sm font-bold font-mulish text-gray-800"
            >
              Lessons{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <MultiSelectDropdown
              id="create-space-lessons"
              options={lessons}
              selected={selectedLessonIds}
              onChange={setSelectedLessonIds}
              placeholder="No lesson — open whiteboard"
              loading={lessonsLoading}
              maxCount={MAX_LESSONS_PER_SPACE}
            />
            <span className="text-xs font-mulish text-gray-400">
              Attach up to {MAX_LESSONS_PER_SPACE} lessons to the canvas.
            </span>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="create-space-description"
              className="text-sm font-bold font-mulish text-gray-800"
            >
              Description{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="create-space-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Warm-up on equivalent fractions for Period 2"
              maxLength={200}
              className={inputClass}
            />
          </div>
        </div>
        {/* Instructions to AI */}
        <div className="et-space-ai-fields">
          <label
            htmlFor="create-space-instructions"
            className="text-sm font-bold font-mulish text-gray-800"
          >
            Instructions to AI{" "}
            <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            id="create-space-instructions"
            value={lessonContext}
            onChange={(e) => setLessonContext(e.target.value)}
            placeholder="e.g. We've been working on equivalent fractions."
            rows={3}
            maxLength={2000}
            className={`${inputClass} resize-y`}
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400 font-mulish">
              The AI uses this to scaffold its responses.
            </span>
            <span className="text-xs text-gray-400 font-mulish">
              {lessonContext.length}/2000
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="et-space-create-actions">
          <button
            type="button"
            disabled={isSavingDraft || isLaunching}
            onClick={() => navigate("/spaces")}
            className="text-sm font-semibold font-mulish text-[#28543D] hover:text-[#204833] transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={isSavingDraft || isLaunching}
              onClick={() => handleCreate(true)}
              className="h-9 px-4 rounded-[6px] border border-[#9AA884] text-[#28543D] text-sm font-semibold font-mulish hover:bg-[#EEF0E3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSavingDraft ? "Saving…" : "Save as draft"}
            </button>
            <button
              type="submit"
              disabled={isSavingDraft || isLaunching}
              className="h-9 px-4 rounded-[6px] bg-[#28543D] hover:bg-[#204833] text-white text-sm font-semibold font-mulish transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLaunching ? "Creating…" : "Launch Space"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
